import { createContext, useContext } from "@bestowinc/tinkertoy-shell-deps/react";
import type { ReactNode } from "@bestowinc/tinkertoy-shell-deps/react";

import type { ModuleLoader } from "./types";

/**
 * React context carrying the shell's {@link ModuleLoader}.
 *
 * Lets components deep in the tree (e.g. the catch-all error boundary) load
 * module bundles without the shell having to prop-drill the loader through
 * every route. Defaults to `null` so consumers can detect a missing provider.
 */
const ModuleLoaderContext = createContext<ModuleLoader | null>(null);

export interface ModuleLoaderProviderProps {
  loader: ModuleLoader;
  children: ReactNode;
}

/**
 * Provides a {@link ModuleLoader} to descendants via
 * {@link ModuleLoaderContext}. Rendered once by `InternalTinkertoyProvider`;
 * not a public export of this package.
 */
export function ModuleLoaderProvider({
  loader,
  children,
}: ModuleLoaderProviderProps) {
  return (
    <ModuleLoaderContext.Provider value={loader}>
      {children}
    </ModuleLoaderContext.Provider>
  );
}

/**
 * Read the shell's {@link ModuleLoader} from context.
 *
 * @throws {Error} if called outside the Tinkertoy shell's provider tree
 * (i.e. outside the tree set up by `ShellRoot` / `InternalTinkertoyProvider`).
 */
export function useModuleLoader(): ModuleLoader {
  const loader = useContext(ModuleLoaderContext);
  if (loader === null) {
    throw new Error(
      "useModuleLoader() must be called inside the Tinkertoy shell. " +
        "Ensure the component is rendered within the tree created by ShellRoot " +
        "(or wrapped in InternalTinkertoyProvider in tests).",
    );
  }
  return loader;
}
